import { getIntegrationsSettings, submitIntegrationsInfo } from '@/api/settings';
import * as atsIds from '@/helpers/constants/atsIds';
import * as atsGuidance from '@/helpers/constants/guidance';
import { TYPE_GR8PEOPLE, TYPE_UMICH, TYPE_AVATURE } from '@/helpers/constants/integrationTypes';
import { useSettingsConfig } from '@/stores/settings/config';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useSettingsIntegrationsForm } from '@/stores/settings/integrationsForm';
import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';

export const useSettingsIntegrations = defineStore('settingsIntegrations', () => {
  // other stores
  const { config } = storeToRefs(useSettingsConfig());
  const { setLoading } = useAppLoading();
  const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();
  const form = useSettingsIntegrationsForm();

  // state
  const submitted = ref(false);
  const alreadySubmitted = ref(false);
  const smartRecruitersInfo = ref({
    hasAuthenticatorUrl: false,
    alreadyConnected: false,
    connectUrl: '',
  });
  const leverInfo = ref({
    hasAuthenticatorUrl: false,
    alreadyConnected: false,
    connectUrl: '',
  });

  const showPermissionError = ref(false);
  const permissionError = ref({
    errorMessages: [], permissionsDetail: {}, permissionsSufficient: false, tokenValid: false,
  });

  // getters
  const submitSuccess = computed(() => (alreadySubmitted.value || submitted.value));
  const supportEmail = computed(() => `${config.value.atsName}@Datapeople.io`);
  const logoUrl = computed(() => `https://static.tapwage.com/taprecruit/ats_icons/ats_${config.value.atsId}.png`);

  const isGreenhouse = computed(
    () => config.value.atsId === atsIds.GREENHOUSE,
  );
  const isSmartRecruiters = computed(() => config.value.atsId === atsIds.SMARTRECRUITERS);
  const isIcims = computed(() => config.value.atsId === atsIds.ICIMS);
  const isLever = computed(() => config.value.atsId === atsIds.LEVER);
  const isWorkday = computed(() => config.value.atsId === atsIds.WORKDAY);
  const isGr8People = computed(() => config.value.atsId === atsIds.GR8PEOPLE);
  const isSuccessFactors = computed(() => config.value.atsId === atsIds.SUCCESSFACTORS);

  const allowIntegratedUserDelete = computed(() => [
    TYPE_GR8PEOPLE, TYPE_UMICH, TYPE_AVATURE,
  ].includes(config.value.integrationTypeKey));

  const allowIntegratedUserInvite = computed(() => [
    TYPE_AVATURE,
  ].includes(config.value.integrationTypeKey));

  const guidance = computed(() => {
    switch (config.value.atsId) {
      case atsIds.GREENHOUSE:
        return atsGuidance.GREENHOUSE_GUIDANCE;
      case atsIds.WORKDAY:
        return atsGuidance.WORKDAY_GUIDANCE;
      case atsIds.SUCCESSFACTORS:
        return atsGuidance.SUCCESSFACTORS_GUIDANCE;
      case atsIds.ICIMS:
        return atsGuidance.ICIMS_GUIDANCE;
      case atsIds.GR8PEOPLE:
        return atsGuidance.GR8PEOPLE_GUIDANCE;
      case atsIds.LEVER:
        return atsGuidance.LEVER_GUIDANCE;
      case atsIds.SMARTRECRUITERS:
        return atsGuidance.SMARTRECRUITERS_GUIDANCE;
      default:
        return null;
    }
  });

  // mutations
  const setSmartRecruitersInfo = (payload) => {
    smartRecruitersInfo.value = {
      hasAuthenticatorUrl: payload.hasAuthenticatorUrl,
      alreadyConnected: payload.alreadyConnected,
      connectUrl: payload.connectUrl,
    };
  };

  const setLeverInfo = (payload) => {
    leverInfo.value = {
      hasAuthenticatorUrl: payload.hasAuthenticatorUrl,
      alreadyConnected: payload.alreadyConnected,
      connectUrl: payload.connectUrl,
    };
  };

  const setSubmitted = (success) => {
    submitted.value = success;
  };

  const setPermissionsError = (response) => {
    if (!response) {
      showPermissionError.value = false;
      permissionError.value = {
        errorMessages: [], permissionsDetail: {}, permissionsSufficient: false, tokenValid: false,
      };
      return;
    }

    showPermissionError.value = true;
    permissionError.value = {
      errorMessages: response.error_messages,
      permissionsDetail: response.permissions_detail,
      permissionsSufficient: response.permissions_sufficient,
      tokenValid: response.token_valid,
    };
  };

  // actions
  const updateIntegrationsInfo = (data) => {
    if (isSmartRecruiters.value) {
      setSmartRecruitersInfo(data);
    }
    if (isLever.value) {
      setLeverInfo(data);
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });
      const { data } = await getIntegrationsSettings();
      updateIntegrationsInfo(data);
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Integration settings', error });

      if (knownError) return;

      addErrorSnackbar({
        text: `Integration settings - ${error.message}`,
      });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const submit = async () => {
    if (form?.value && !form?.isFormValid) {
      return;
    }

    try {
      setLoading({ componentLoading: true });
      const response = await submitIntegrationsInfo({ token: form.values.token });

      if (response.success) {
        setSubmitted(true);
        addSuccessSnackbar({
          text: `Your ${config.value.atsName} integration information has been submitted`,
        });
      }
    } catch (error) {
      const { response } = error;
      if (response && response.code === 'validation_failed') {
        form.setBackendErrors(response.errors);
      } else if (response && response.code === 'permissions_failed') {
        setPermissionsError(response.token_permissions || {});
      } else {
        const knownError = handleKnownSettingsApiErrors({ context: 'Integration settings', error });

        if (knownError) return;

        // catch-all error handling for generic server errors
        addErrorSnackbar({ text: `Integration settings - ${error.message}` });
        throw error;
      }
    } finally {
      setLoading({ componentLoading: false });
    }
  };

  return {
    // state
    submitted,
    alreadySubmitted,
    smartRecruitersInfo,
    leverInfo,
    showPermissionError,
    permissionError,

    // getters
    submitSuccess,
    supportEmail,
    logoUrl,
    isSmartRecruiters,
    isLever,
    isWorkday,
    isGr8People,
    isGreenhouse,
    isIcims,
    isSuccessFactors,
    guidance,
    allowIntegratedUserDelete,
    allowIntegratedUserInvite,

    // mutations
    setSmartRecruitersInfo,
    setLeverInfo,
    setSubmitted,
    setPermissionsError,

    // actions
    updateIntegrationsInfo,
    load,
    submit,
  };
});
